import * as React from 'react';
import { useFeatures } from '../../providers/FeaturesProvider';
import { useContext, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  UserIcon,
  CubeIcon,
  GlobeAltIcon,
  ComputerDesktopIcon,
  ServerStackIcon,
  Square3Stack3DIcon,
  ClipboardDocumentListIcon,
  EnvelopeIcon,
  EnvelopeOpenIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  SignalIcon,
  CircleStackIcon,
  PaperAirplaneIcon,
  ArrowTopRightOnSquareIcon,
  ShieldCheckIcon,
  ArrowRightOnRectangleIcon,
  Squares2X2Icon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import { KeycloakContext } from '../../App';
import { Sidebar, type NavSection } from './Sidebar';
import { Topbar } from './Topbar';
import { ThemeToggle } from '../ui/theme-toggle';
import { useTheme } from '../../providers/ThemeProvider';
import { ApiStatusIndicator, AppFooter } from '../features/ApiStatus';
import { PatchNotesButton } from '../features/PatchNotes';
import { AIChatbot } from '../features/AIChatbot';
import { Dropdown } from '../ui/dropdown';
import { cn } from '../../lib/cn';

const STORAGE_KEY_COLLAPSED = 'edj-dashboard-sidebar-collapsed';

function Logo({ compact }: { compact?: boolean }) {
  const { resolvedTheme } = useTheme();
  const logoSrc = resolvedTheme === 'dark'
    ? '/img/edj-labs-logo-transparent-light.png'
    : '/img/edj-labs-logo-transparent-dark.png';
  if (compact) {
    return (
      <Link to="/services" className="flex h-9 w-9 items-center justify-center">
        <img src={logoSrc} alt="EDJ-LABS" className="h-8 w-8 object-contain" />
      </Link>
    );
  }
  return (
    <Link to="/services" className="flex items-center">
      <img src={logoSrc} alt="EDJ-LABS" className="h-8 w-auto max-w-[160px] object-contain" />
    </Link>
  );
}

function UserMenu() {
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const navigate = useNavigate();
  const username = (keycloak?.tokenParsed as any)?.preferred_username || (keycloak?.tokenParsed as any)?.email || 'Account';
  const firstName = (keycloak?.tokenParsed as any)?.given_name || '';
  const lastName = (keycloak?.tokenParsed as any)?.family_name || '';
  const displayName = [firstName, lastName].filter(Boolean).join(' ') || username;
  const initials = firstName && lastName
    ? (firstName[0] + lastName[0]).toUpperCase()
    : (displayName || 'A')
        .split(/[.\s_-]+/)
        .map((s: string) => s[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();

  const isAdmin = keycloak?.tokenParsed?.realm_access?.roles?.includes('admin') || false;

  return (
    <Dropdown
      trigger={
        <button
          type="button"
          className="flex items-center gap-2 rounded-full border border-border bg-card p-0.5 pr-0.5 sm:pr-3 transition-colors hover:bg-accent"
          aria-label="Account menu"
        >
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
            {initials}
          </span>
          <span className="hidden max-w-[160px] truncate text-xs font-medium text-foreground sm:inline">{displayName}</span>
        </button>
      }
      align="right"
      items={[
        { label: 'Profile', icon: <UserIcon />, onSelect: () => navigate('/profile') },
        { label: 'Bills', icon: <DocumentTextIcon />, onSelect: () => navigate('/bills') },
        { label: 'Support', icon: <ChatBubbleLeftRightIcon />, onSelect: () => navigate('/support') },
        ...(isAdmin ? [{ separator: true }, { label: 'Administration', icon: <ShieldCheckIcon />, onSelect: () => navigate('/admin') }] : []),
        { separator: true },
        { label: 'Sign out', icon: <ArrowRightOnRectangleIcon />, danger: true, onSelect: () => keycloak?.logout({ redirectUri: window.location.origin }) },
      ]}
    />
  );
}

export default function DashboardLayout() {
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return window.localStorage.getItem(STORAGE_KEY_COLLAPSED) === '1'; } catch { return false; }
  });

  React.useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY_COLLAPSED, collapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [collapsed]);

  // Close mobile sidebar on route change
  React.useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  const isAdmin = keycloak?.tokenParsed?.realm_access?.roles?.includes('admin') || false;
  const userKeycloakId = String((keycloak?.tokenParsed as any)?.UserKeycloakID || keycloak?.tokenParsed?.sub || '');
  const mongoExpressUrl = userKeycloakId
    ? `https://mongo.edj-labs.com/?filter=${encodeURIComponent(userKeycloakId)}`
    : 'https://mongo.edj-labs.com/';
  const externalTools = useMemo(
    () => [{ name: 'Mongo Express', href: mongoExpressUrl }],
    [mongoExpressUrl]
  );

  const { isEnabled } = useFeatures();

  const sections: NavSection[] = useMemo<NavSection[]>(
    () => [
      {
        label: 'Platform',
        items: [
          { name: 'Services', href: '/services', icon: CubeIcon, feature: 'api-service' },
          { name: 'Status', href: '/status', icon: SignalIcon, feature: 'api-status' },
          { name: 'Orders', href: '/orders', icon: ShoppingCartIcon, feature: 'api-order' },
          { name: 'Bills', href: '/bills', icon: DocumentTextIcon, feature: 'api-billing' },
        ],
      },
      {
        label: 'Web',
        items: [
          { name: 'Domains', href: '/domains', icon: GlobeAltIcon, feature: 'api-domain' },
          { name: 'Websites', href: '/websites', icon: ComputerDesktopIcon, feature: 'api-website' },
          { name: 'Emails', href: '/emails', icon: EnvelopeIcon, feature: 'api-email' },
        ],
      },
      {
        label: 'Infrastructure',
        items: [
          { name: 'Containers', href: '/containers', icon: Square3Stack3DIcon, feature: 'api-container' },
          { name: 'VPS', href: '/vps', icon: ServerStackIcon, feature: 'api-vps' },
          { name: 'Databases', href: '/databases', icon: CircleStackIcon, feature: 'api-database' },
          { name: 'Messaging', href: '/messages', icon: PaperAirplaneIcon, feature: 'api-messaging' },
          { name: 'Emailing', href: '/emailing', icon: EnvelopeOpenIcon, feature: 'api-emailing' },
          { name: 'WhatsApp', href: '/whatsapp', icon: ChatBubbleOvalLeftEllipsisIcon, feature: 'api-whatsapp' },
        ],
      },
      {
        label: 'Account',
        items: [
          { name: 'Profile', href: '/profile', icon: UserIcon },
          { name: 'Support', href: '/support', icon: ChatBubbleLeftRightIcon, feature: 'api-support' },
          ...(isAdmin ? [{ name: 'Administration', href: '/admin', icon: ShieldCheckIcon }] : []),
        ],
      },
      {
        label: 'External tools',
        items: externalTools.map((t) => ({
          name: t.name,
          href: t.href,
          icon: ArrowTopRightOnSquareIcon,
          external: true,
        })),
      },
    ],
    [isAdmin, externalTools]
  )
    .map((section) => ({ ...section, items: section.items.filter((item) => isEnabled(item.feature)) }))
    .filter((section) => section.items.length > 0);

  const sidebarFooter = <AppFooter />;

  return (
    <div className="min-h-screen bg-background">
      <Sidebar
        sections={sections}
        open={sidebarOpen}
        onOpenChange={setSidebarOpen}
        collapsed={collapsed}
        onCollapsedChange={setCollapsed}
        logoFull={<Logo />}
        logoCompact={<Logo compact />}
        footer={collapsed ? null : sidebarFooter}
      />

      <div className={cn('transition-[padding] duration-200', collapsed ? 'lg:pl-16' : 'lg:pl-64')}>
        <Topbar
          onOpenSidebar={() => setSidebarOpen(true)}
          right={
            <>
              <ApiStatusIndicator className="hidden sm:inline-flex" variant="pill" />
              <ApiStatusIndicator className="sm:hidden" variant="dot" />
              <PatchNotesButton />
              <ThemeToggle />
              <UserMenu />
            </>
          }
        />
        <main className="px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <div className="mx-auto w-full max-w-7xl space-y-6">
            <Outlet />
          </div>
        </main>
      </div>

      <AIChatbot />
    </div>
  );
}
